import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";

/**
 * アカウント一覧のインライン編集セル。
 *
 * Table の規約（セル内コントロールは枠線なし、編集中のフィードバックは
 * `td:focus-within` の青いリング）に従う borderless input。
 * blur / Enter で確定、Escape で元の値に戻す。保存失敗時は呼び出し側で
 * reject し、このセルが元の値へ戻す。
 */
const CellInput = styled.input`
  /* && でグローバルの input[type="text"]（枠線・padding・focus 装飾）より
     詳細度を上げる。編集フィードバックは td:focus-within のリングに任せる。 */
  && {
    display: block;
    width: 100%;
    padding: 0;
    margin: 0;
    border: none;
    border-radius: 0;
    outline: none;
    box-shadow: none;
    background: transparent;
    font: inherit;
    color: inherit;
  }

  &&:focus {
    border: none;
    box-shadow: none;
    font-size: inherit;
  }

  &&::placeholder {
    color: var(--soremo-light-gray);
  }

  &&:disabled {
    cursor: wait;
  }
`;

interface EditableTextCellProps {
  value: string | null | undefined;
  /** 確定時に呼ばれる。reject すると元の値に戻す。 */
  onSave: (value: string) => Promise<void>;
  ariaLabel: string;
  placeholder?: string;
}

const EditableTextCell: React.FC<EditableTextCellProps> = ({
  value,
  onSave,
  ariaLabel,
  placeholder = "—",
}) => {
  const original = value ?? "";
  const [draft, setDraft] = useState(original);
  const [isSaving, setIsSaving] = useState(false);
  const focusedRef = useRef(false);
  const cancelledRef = useRef(false);

  // 外部（再フェッチ等）で値が変わったら、編集中でなければ同期する。
  useEffect(() => {
    if (!focusedRef.current) setDraft(original);
  }, [original]);

  const commit = async (nextValue: string) => {
    if (cancelledRef.current) {
      cancelledRef.current = false;
      setDraft(original);
      return;
    }
    if (nextValue === original || isSaving) return;
    setIsSaving(true);
    try {
      await onSave(nextValue);
    } catch {
      setDraft(original);
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.currentTarget.blur();
    } else if (e.key === "Escape") {
      cancelledRef.current = true;
      e.currentTarget.blur();
    }
  };

  return (
    <CellInput
      type="text"
      value={draft}
      aria-label={ariaLabel}
      placeholder={placeholder}
      disabled={isSaving}
      onChange={(e) => setDraft(e.target.value)}
      onFocus={() => {
        focusedRef.current = true;
      }}
      onBlur={(e) => {
        focusedRef.current = false;
        void commit(e.target.value);
      }}
      onKeyDown={handleKeyDown}
    />
  );
};

export default EditableTextCell;
